'use client'

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

import { usePathname } from 'next/navigation'

import { cn } from '@/lib/utils'

type HeaderChrome = {
  setMegaOpen: (open: boolean) => void

  /** Толгой хатуу (цагаан дэвсгэртэй) үед үнэн. Логоны өнгө үүнээс хамаарна. */
  solid: boolean
}

// Толгойноос ГАДУУР (ж: хөл хэсэг) дуудагдвал үргэлж хатуу төлөв гэж үзнэ.
const HeaderChromeContext = createContext<HeaderChrome>({ setMegaOpen: () => {}, solid: true })

/** Цэсний том самбар нээгдэхэд толгойг тунгалагаас гаргахад ашиглана. */
export const useHeaderChrome = () => useContext(HeaderChromeContext)

/**
 * Толгойн хэсгийн дүр төрхийг хуудас ба гүйлгэлтээс хамааруулна.
 *
 * Нүүр хуудсанд толгой нь ЭХЛЭЭД ТУНГАЛАГ, бүтэн дэлгэцийн слайдер дээр
 * хөвж харагдана (company.namyangi.com-ын жишгээр). Доош гүйлгэмэгц цагаан
 * дэвсгэртэй болж бичиг нь хар болно.
 *
 * ЯАГААД `fixed`: `sticky` нь урсгалд зай эзэлдэг тул слайдер толгойн ДООР
 * эхэлнэ. `fixed` нь урсгалаас гардаг учир слайдер дэлгэцийн дээд ирмэгээс
 * эхэлж, толгой нь дээр нь хөвнө.
 *
 * ЯАГААД самбар нээгдэхэд хатуу болгодог вэ: тунгалаг үед бүх холбоос, товчийг
 * үр удмаар нь цагаан болгодог. Уналтын самбар нь толгойн ДОТОР байрладаг тул
 * тэр дүрэм самбарын бичгийг ч цагаан болгож, цагаан дэвсгэр дээр алга болгоно.
 * Самбар нээлттэй үед хатуу төлөвт шилжүүлснээр тэр дүрэм огт хэрэглэгдэхгүй.
 *
 * ЯАГААД `--foreground` токеныг дарж бичээгүй вэ: `[--foreground:#fff]` гэсэн
 * arbitrary property-г Tailwind энэ тохиргоонд CSS дүрэм болгож гаргадаггүй
 * нь шалгагдсан — класс нь элемент дээр гарч ирдэг ч дүрэм үүсдэггүй.
 */
export const SiteHeaderShell = ({ children }: { children: ReactNode }) => {
  const pathname = usePathname()
  const [scrolled, setScrolled] = useState(false)
  const [megaOpen, setMegaOpen] = useState(false)

  // Зөвхөн нүүр хуудас бүтэн дэлгэцийн слайдертай тул зөвхөн тэнд хөвнө.
  const overlay = pathname === '/'

  useEffect(() => {
    if (!overlay) return

    const sync = () => setScrolled(window.scrollY > 8)

    // Хуудсыг сэргээхэд гүйлгэлт 0 биш байж болно — mount дээр нэг удаа шалгана.
    sync()
    window.addEventListener('scroll', sync, { passive: true })

    return () => window.removeEventListener('scroll', sync)
  }, [overlay])

  const solid = !overlay || scrolled || megaOpen

  return (
    <HeaderChromeContext.Provider value={{ setMegaOpen, solid }}>
      <header
        className={cn(
          'z-50 transition-colors duration-300',
          overlay ? 'fixed inset-x-0 top-0' : 'sticky top-0',
          solid
            ? 'bg-background/80 border-b backdrop-blur'
            : cn(
                'border-b border-transparent text-white [&_a]:text-white [&_button]:text-white',

                /*
                  Толгойн зарим товч (ханшийн сэлгүүр) `outline` хэлбэртэй тул
                  ЦАГААН дэвсгэртэй байдаг — тунгалаг үед цагаан дүрстэйгээ
                  нийлж үл үзэгдэх цагаан дөрвөлжин болно.
                */
                '[&_button]:border-white/35 [&_button]:bg-transparent'
              )
        )}
      >
        {children}
      </header>
    </HeaderChromeContext.Provider>
  )
}
